import { use, useEffect, useState } from "react";
import { useUser } from "../contexts/UserContext";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router";
import { ROUTES } from "../App";
import { onValue, ref } from "firebase/database";
import { database } from "../constants/firebase";

const ViewUsersModal = ({ isOpen, onClose, users, type }) => {
    const { currentUser } = useUser();
    const navigate = useNavigate();
    const [userLists, setUserLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imageLoading, setImageLoading] = useState({});

    useEffect(() => {
        if (!isOpen || !users) return;

        setLoading(true);

        console.log("Users: ", users);

        // Get all users UIDs for the user    
        const usersUids = users;
        // Clear current users before loading
        setUserLists([]);

        // Fetch user details for each user UID and update the list
        for (const uid of usersUids) {
            try {
                const userRef = ref(database, `Users/${uid}`);
                onValue(userRef, (userSnapshot) => {
                    const userData = userSnapshot.val();
                    if (userData) {
                        // Check if user already exists in the list to avoid duplicates
                        setUserLists(prev => {
                            const existing = prev.find(f => f.uid === uid);
                            if (existing) return prev;

                            return [...prev.filter(f => f.uid !== uid), {
                                uid,
                                name: userData.name || userData.email || 'Unknown User',
                                profilePic: userData.profilePic || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef',
                                email: userData.email,
                                role: userData.role,
                                niche: userData.niche || 'No niche specified',
                            }].sort((a, b) => {
                                return a.name.localeCompare(b.name);
                            });
                        });
                    }
                });
            } catch (error) {
                console.error("Error fetching user data:", error);
            } finally {
                setLoading(false);
            }
        }


        return () => {
            setUserLists([]);
        };
    }, [isOpen, users]);

    const handleClose = () => {
        onClose();
    };

    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(6)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-3"
                >
                    <div className="flex items-center space-x-2">
                        <div className="w-12 h-12 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                        <div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-black dark:text-white">
                        {type === "friends" ? "Friends" : "Members"}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        <IoMdCloseCircleOutline size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Members List */}
                <div className="overflow-y-auto max-h-[60vh]">
                    {users.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {type === "friends" ? "No friends found" : "No members found"}
                        </div>
                    ) : (
                        <div className="p-4 space-y-3">
                            {loading ?
                                <ShimmerLoader /> :
                                userLists.map((user) => {
                                    const isLoading = imageLoading[user.uid] !== false; // default true

                                    return (
                                        <button
                                            key={user.uid}
                                            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer w-full"
                                            onClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${user.uid}`)}
                                        >
                                            <div className="relative w-12 h-12">
                                                {isLoading && (
                                                    <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                                                )}
                                                <img
                                                    src={user.profilePic}
                                                    alt={user.name}
                                                    className={`w-12 h-12 rounded-full object-cover transition-opacity duration-300 ${!isLoading ? 'opacity-100' : 'opacity-0'}`}
                                                    onLoad={() => setImageLoading(prev => ({ ...prev, [user.uid]: false }))}
                                                    onError={() => setImageLoading(prev => ({ ...prev, [user.uid]: false }))}
                                                />
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2">
                                                    <p className={`flex items-center gap-2 font-medium ${user?.role === "Host" ? "dark:glow-text-dark glow-text-light" : "text-black dark:text-white"} truncate`}>
                                                        {user.name}
                                                    </p>
                                                    {user.uid === currentUser?.uid && (
                                                        <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-full">You</span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate text-left">{user.niche}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                        </div>

                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                        {users.length} {users.length === 1 ? (type === "friends" ? 'friend' : 'member') : (type === "friends" ? 'friends' : 'members')}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default ViewUsersModal;